import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './notifications.css';
import Layout from '../components/layout';
import { supabase } from '../supabase';
import bell from '../assets/bell.svg';
import { useGsapAnimations } from '../hooks/useGsapAnimations';

const screenRoutes = {
  bookings: '/booking',
  booking: '/booking',
  schedule: '/schedule',
  map: '/map',
  tickets: '/ticket',
  ticket: '/ticket',
  home: '/home',
};

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const pageRef = useRef(null);
  const navigate = useNavigate();

  useGsapAnimations(pageRef, notifications.length);

  const unreadCount = useMemo(
    () => notifications.filter((notification) => !notification.is_read).length,
    [notifications]
  );

  useEffect(() => {
    let ignore = false;

    const getNotifications = async () => {
      setLoading(true);
      setError('');

      const { data, error } = await supabase
        .from('notifications')
        .select('id, created_at, title, message, type, target_screen, is_read')
        .order('created_at', { ascending: false });

      if (ignore) return;

      if (error) {
        setError(error.message);
        setNotifications([]);
      } else {
        setNotifications(data || []);
      }

      setLoading(false);
    };

    getNotifications();

    return () => {
      ignore = true;
    };
  }, []);

  const markAsRead = async (notification) => {
    if (notification.is_read) return;

    setNotifications((currentNotifications) =>
      currentNotifications.map((item) =>
        item.id === notification.id ? { ...item, is_read: true } : item
      )
    );

    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('id', notification.id);

    if (error) {
      setNotifications((currentNotifications) =>
        currentNotifications.map((item) =>
          item.id === notification.id ? { ...item, is_read: false } : item
        )
      );
      setError(error.message);
    }
  };

  const markAllAsRead = async () => {
    const unreadIds = notifications
      .filter((notification) => !notification.is_read)
      .map((notification) => notification.id);

    if (!unreadIds.length) return;

    setNotifications((currentNotifications) =>
      currentNotifications.map((notification) => ({ ...notification, is_read: true }))
    );

    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .in('id', unreadIds);

    if (error) {
      setError(error.message);
    }
  };

  const openNotification = async (notification) => {
    await markAsRead(notification);

    const route = screenRoutes[notification.target_screen];

    if (route) {
      navigate(route);
    }
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return '';

    return new Intl.DateTimeFormat('en', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateValue));
  };

  return (
    <Layout>
      <div className="notifications_page" ref={pageRef}>
        <section className="notifications_header" data-gsap-intro>
          <p>NOTIFICATIONS</p>
          <div className="notifications_title_row">
            <h1>Updates</h1>
            <span>{unreadCount} unread</span>
          </div>
        </section>

        <section className="notifications_summary" data-gsap-reveal data-gsap-scale="0.94">
          <div className="notifications_summary_icon">
            <img src={bell} alt="" />
          </div>
          <div>
            <p>Latest from Techne</p>
            <h2>{loading ? 'Syncing notifications' : `${notifications.length} total updates`}</h2>
          </div>
          <button type="button" onClick={markAllAsRead} disabled={!unreadCount} aria-label="Mark all notifications as read">
            Read all
          </button>
        </section>

        {error && <p className="notifications_error" role="alert">{error}</p>}

        {loading ? (
          <section className="notifications_list">
            {[1, 2, 3].map((item) => (
              <article className="notification_card notification_skeleton" key={item}>
                <span />
                <div />
                <div />
              </article>
            ))}
          </section>
        ) : notifications.length ? (
          <section
            className="notifications_list"
            data-gsap-stagger=".notification_card"
            data-gsap-start="top 92%"
          >
            {notifications.map((notification) => (
              <article
                className={`notification_card ${notification.is_read ? 'read_notification' : ''}`}
                key={notification.id}
              >
                <button type="button" onClick={() => openNotification(notification)} aria-label={`Open notification: ${notification.title}`}>
                  <div className="notification_card_top">
                    <span className="notification_type">{notification.type || 'general'}</span>
                    <time dateTime={notification.created_at}>{formatDate(notification.created_at)}</time>
                  </div>

                  <div className="notification_card_body">
                    <span className="notification_dot" />
                    <div>
                      <h2>{notification.title}</h2>
                      <p>{notification.message}</p>
                    </div>
                  </div>
                </button>
              </article>
            ))}
          </section>
        ) : (
          <section className="notifications_empty" data-gsap-reveal>
            <img src={bell} alt="" />
            <h2>No notifications yet</h2>
            <p>New event updates and booking alerts will show here.</p>
          </section>
        )}
      </div>
    </Layout>
  );
};

export default Notifications;
